import React, { useState } from 'react';
import { Trash2, Download, AlertTriangle, Key, Save, Shield } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AccountManagement = ({ user, userProfile }) => {
  const { signOut } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState({
    export: false,
    delete: false,
    password: false
  });
  const [errors, setErrors] = useState({});

  const handleDataExport = async () => {
    setLoading(prev => ({ ...prev, export: true }));
    
    try {
      // In a real app, you'd call an API to generate and send the export
      // For now, we'll simulate the process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert('Data export request submitted successfully! You will receive an email with your data within 24 hours.');
    } catch (error) {
      console.error('Error requesting data export:', error);
      alert('Failed to request data export. Please try again.');
    } finally {
      setLoading(prev => ({ ...prev, export: false }));
    }
  };

  const handlePasswordChange = async (e) => {
    e?.preventDefault();
    setErrors({});

    // Validate form
    const newErrors = {};
    if (!passwordForm?.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    if (!passwordForm?.newPassword || passwordForm?.newPassword?.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }
    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(prev => ({ ...prev, password: true }));
    
    try {
      const { error } = await supabase?.auth?.updateUser({
        password: passwordForm?.newPassword
      });

      if (error) throw error;

      alert('Password updated successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordChange(false);
    } catch (error) {
      console.error('Error updating password:', error);
      setErrors({ currentPassword: 'Failed to update password. Please check your current password.' });
    } finally {
      setLoading(prev => ({ ...prev, password: false }));
    }
  };

  const handleAccountDeletion = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setErrors({ delete: 'Please type DELETE to confirm' });
      return;
    }

    setLoading(prev => ({ ...prev, delete: true }));
    
    try {
      // In a real app, you'd call an API to handle account deletion
      // This would involve deleting user data, anonymizing records, etc.
      
      // For now, we'll just sign out the user
      await signOut();
      alert('Account deletion initiated. You will be contacted via email to complete the process.');
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Failed to delete account. Please contact support.');
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  return (
    <div className="p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Shield className="h-5 w-5 mr-2" />
          Account Management
        </h2>
        <p className="text-gray-600 mt-1">
          Manage your account security and data
        </p>
      </div>
      <div className="space-y-8">
        {/* Account Information */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Account ID</label>
              <p className="text-sm text-gray-900 font-mono">{user?.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-sm text-gray-900">{user?.email}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="text-sm text-gray-900">
                {user?.created_at ? new Date(user?.created_at)?.toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Sign In</label>
              <p className="text-sm text-gray-900">
                {user?.last_sign_in_at ? new Date(user?.last_sign_in_at)?.toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Password Change */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Key className="h-5 w-5 mr-2" />
                Change Password
              </h3>
              <p className="text-sm text-gray-600">Update your account password</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowPasswordChange(!showPasswordChange)}
            >
              {showPasswordChange ? 'Cancel' : 'Change Password'}
            </Button>
          </div>

          {showPasswordChange && (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <Input
                  type="password"
                  value={passwordForm?.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e?.target?.value }))}
                  error={errors?.currentPassword}
                  className="max-w-md"
                />
                {errors?.currentPassword && (
                  <p className="text-red-600 text-sm mt-1">{errors?.currentPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <Input
                  type="password"
                  value={passwordForm?.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e?.target?.value }))}
                  error={errors?.newPassword}
                  className="max-w-md"
                />
                {errors?.newPassword && (
                  <p className="text-red-600 text-sm mt-1">{errors?.newPassword}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <Input
                  type="password"
                  value={passwordForm?.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                  error={errors?.confirmPassword}
                  className="max-w-md"
                />
                {errors?.confirmPassword && (
                  <p className="text-red-600 text-sm mt-1">{errors?.confirmPassword}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading?.password}
                className="flex items-center"
              >
                {loading?.password ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {loading?.password ? 'Updating...' : 'Update Password'}
              </Button>
            </form>
          )}
        </div>

        {/* Data Export */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Download className="h-5 w-5 mr-2" />
                Export Your Data
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Download a copy of all your data in JSON format
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleDataExport}
              disabled={loading?.export}
              className="flex items-center"
            >
              {loading?.export ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {loading?.export ? 'Requesting...' : 'Request Export'}
            </Button>
          </div>
        </div>

        {/* Account Deletion */}
        <div className="border border-red-200 rounded-lg p-6 bg-red-50">
          <div className="flex items-start">
            <AlertTriangle className="h-6 w-6 text-red-600 mt-1 mr-3" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-red-900 mb-2">Delete Account</h3>
              <p className="text-sm text-red-700 mb-4">
                This action cannot be undone. This will permanently delete your account and remove
                all associated data from our servers.
              </p>

              {!showDeleteConfirm ? (
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete My Account
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-red-900 mb-2">
                      Type "DELETE" to confirm account deletion:
                    </label>
                    <Input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e?.target?.value)}
                      placeholder="DELETE"
                      className="max-w-xs"
                      error={errors?.delete}
                    />
                    {errors?.delete && (
                      <p className="text-red-600 text-sm mt-1">{errors?.delete}</p>
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      onClick={handleAccountDeletion}
                      disabled={loading?.delete}
                      className="bg-red-600 hover:bg-red-700 text-white flex items-center"
                    >
                      {loading?.delete ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      {loading?.delete ? 'Deleting...' : 'Confirm Deletion'}
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                        setErrors({});
                      }}
                      className="border-gray-300"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;